import React, { useState, useRef, useEffect } from 'react';
import './styles.css';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  bordered?: boolean;
  style?: React.CSSProperties;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  defaultValue,
  value,
  onChange,
  bordered = true,
  style
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options.find(option => option.value === (value || defaultValue)) || options[0]
  );
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      const option = options.find(opt => opt.value === value);
      if (option) {
        setSelectedOption(option);
      }
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option: Option) => {
    setSelectedOption(option);
    setIsOpen(false);
    onChange?.(option.value);
  };

  return (
    <div
      className={`custom-select-container ${bordered ? '' : 'custom-select-borderless'}`}
      ref={selectRef}
      style={style}
    >
      <div
        className={`custom-select-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="custom-select-selected-value">{selectedOption.label}</div>
        <div className="custom-select-arrow">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d={isOpen ? "M7 15l5-5 5 5z" : "M7 10l5 5 5-5z"} />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="custom-select-dropdown">
          {options.map(option => (
            <div
              key={option.value}
              className={`custom-select-option ${
                option.value === selectedOption.value ? 'selected' : ''
              }`}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
